import { useEffect, useState } from "react";
import loginStyles from "../../../pages/login/login.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { loginEmailValidationSchema } from "../../../utils/validation";
import { authApi } from "../../../services/api/auth.api";
import { errorUserSelector, errorTimeStampSelector } from "../../../services/selectors/auth.selectors";
import { useAppSelector } from "../../../services/hooks";
import { useActions } from "../../../services/hooks/useActions";

function LoginEmailForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const from: string = location.state?.from || "/home";

  const [login, { isError }] = authApi.useLoginMutation();
  const loginError = useAppSelector(errorUserSelector);
  const retryAfterTimestamp = useAppSelector(errorTimeStampSelector);

  const { clearRetryAfter } = useActions();

  const [countdown, setCountdown] = useState<number | null>(null);

  const handleLogin = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    const response = await login({ email, password });
    if ("data" in response && response.data.success) {
      navigate(from, { state: { from }, replace: true });
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginEmailValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      // Handle form submission
      handleLogin(formik.values);
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (retryAfterTimestamp) {
      const interval = setInterval(() => {
        const secondsLeft = Math.max(0, Math.floor((retryAfterTimestamp - Date.now()) / 1000));
        setCountdown(secondsLeft);

        if (secondsLeft <= 0) {
          clearInterval(interval);
          setCountdown(null);
          clearRetryAfter(); // Custom action to reset retryAfterTimestamp
        }
      }, 1000);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryAfterTimestamp]);

  return (
    <form onSubmit={formik.handleSubmit} className={loginStyles.login__form}>
      <input
        type="email"
        required={true}
        className={`${loginStyles.login__input} ${loginStyles.login__input_email
          } ${formik.errors.email && loginStyles.login__input_invalid}`}
        name="email"
        id="email"
        placeholder="דואר אלקטרוני"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      />
      <input
        type="password"
        required={true}
        className={`${loginStyles.login__input} ${formik.errors.password && loginStyles.login__input_invalid
          }`}
        name="password"
        id="password"
        placeholder="סיסמה"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      />
      {formik.errors && (
        <p className={`${loginStyles.login__invalid_message}`}>
          {formik.errors.email ?? formik.errors.password}
        </p>
      )}
      {isError && (
        <p className={`${loginStyles.login__invalid_message}`}>{loginError}</p>
      )}
      {/*<Link*/}
      {/*  to={"/restore_password"}*/}
      {/*  className={loginStyles.login__restorePasswordLink}*/}
      {/*>*/}
      {/*  שכחת סיסמה?*/}
      {/*</Link>*/}
      <button
        type="submit"
        disabled={
          (countdown !== null && countdown > 0) ||
          !formik.values.email || !formik.isValid || formik.isSubmitting
        }
        className={`button ${loginStyles.login__button} ${(countdown !== null && countdown > 0) ||
            !formik.values.email || !formik.isValid || formik.isSubmitting
            ? loginStyles.login__button_disabled
            : "hover"
          }`}
      >
        {formik.isSubmitting ? "שליחה" : "כניסה"} {countdown !== null && countdown > 0 ? `(${countdown})` : ''}
      </button>
    </form>
  );
}

export default LoginEmailForm;
